import { useEffect, useRef, useState, useCallback } from 'react';
import { axiosInstance } from '../App';

/**
 * Hook to track user inactivity and trigger auto-logout
 * @param {object} user - Current logged-in user
 * @param {function} onLogout - Callback to execute when auto-logout is triggered
 */
export const useInactivityTimeout = (user, onLogout) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeoutMinutes, setTimeoutMinutes] = useState(null); // Start as null until loaded
  const [warningSeconds, setWarningSeconds] = useState(30);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  const timeoutIdRef = useRef(null);
  const warningTimeoutIdRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const onLogoutRef = useRef(onLogout);
  
  // Keep onLogout ref updated
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  // Fetch timeout settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axiosInstance.get('/settings/system');
        const newTimeoutMinutes = response.data.session?.timeout_minutes || 5;
        const newWarningSeconds = response.data.session?.warning_seconds || 30;
        console.log(`[Inactivity] Settings loaded from server: timeout=${newTimeoutMinutes}min, warning=${newWarningSeconds}s`);
        setTimeoutMinutes(newTimeoutMinutes);
        setWarningSeconds(newWarningSeconds);
        setSettingsLoaded(true);
      } catch (error) {
        console.log('[Inactivity] Failed to load settings, using defaults (5 min)');
        setTimeoutMinutes(5);
        setWarningSeconds(30);
        setSettingsLoaded(true);
      }
    };
    
    if (user) {
      fetchSettings();
    } else {
      // Reset when user logs out
      setSettingsLoaded(false);
      setTimeoutMinutes(null);
    }
  }, [user]);

  // Handle logout
  const handleLogout = useCallback(() => {
    console.log('[Inactivity] Auto-logout triggered!');
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    if (warningTimeoutIdRef.current) clearTimeout(warningTimeoutIdRef.current);
    setShowWarning(false);
    if (onLogoutRef.current) onLogoutRef.current();
  }, []);

  // Reset timer with current settings
  const resetTimer = useCallback((currentTimeout, currentWarning) => {
    // Clear existing timers
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    if (warningTimeoutIdRef.current) {
      clearTimeout(warningTimeoutIdRef.current);
      warningTimeoutIdRef.current = null;
    }

    setShowWarning(false);
    lastActivityRef.current = Date.now();

    // Calculate times in milliseconds
    const warningTime = (currentTimeout * 60 - currentWarning) * 1000;
    const logoutTime = currentTimeout * 60 * 1000;

    console.log(`[Inactivity] Timer set - warning in ${(warningTime/1000).toFixed(0)}s, logout in ${(logoutTime/1000).toFixed(0)}s`);

    // Set warning timer
    warningTimeoutIdRef.current = setTimeout(() => {
      console.log('[Inactivity] ⚠️ Showing warning modal now!');
      setShowWarning(true);
    }, warningTime);

    // Set logout timer
    timeoutIdRef.current = setTimeout(() => {
      console.log('[Inactivity] ⏰ Timeout reached, logging out!');
      handleLogout();
    }, logoutTime);
  }, [handleLogout]);

  // Handle user activity - throttled
  const handleActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Only reset timer if more than 5 seconds since last reset
    // This prevents excessive timer resets
    if (timeSinceLastActivity > 5000 && timeoutMinutes !== null) {
      console.log(`[Inactivity] Activity detected after ${(timeSinceLastActivity/1000).toFixed(0)}s, resetting timer`);
      resetTimer(timeoutMinutes, warningSeconds);
    }
  }, [timeoutMinutes, warningSeconds, resetTimer]);

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = useCallback(() => {
    console.log('[Inactivity] User chose to stay logged in');
    setShowWarning(false);
    if (timeoutMinutes !== null) {
      resetTimer(timeoutMinutes, warningSeconds);
    }
  }, [timeoutMinutes, warningSeconds, resetTimer]);

  // Main effect - initialize timer when settings are loaded
  useEffect(() => {
    // Don't start until we have user AND settings
    if (!user || !settingsLoaded || timeoutMinutes === null) {
      return;
    }
    
    // Don't enable on ratebook (login) page
    if (window.location.hash === '#/ratebook' || window.location.pathname === '/ratebook') {
      console.log('[Inactivity] On ratebook page, timeout disabled');
      return;
    }

    console.log(`[Inactivity] ✅ Initializing with ${timeoutMinutes}min timeout`);

    // Activity events to track
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Start the timer
    resetTimer(timeoutMinutes, warningSeconds);

    // Add activity listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, true);
    });

    // Cleanup on unmount or dependency change
    return () => {
      console.log('[Inactivity] Cleaning up timers and listeners');
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      if (warningTimeoutIdRef.current) {
        clearTimeout(warningTimeoutIdRef.current);
        warningTimeoutIdRef.current = null;
      }
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity, true);
      });
    };
  }, [user, settingsLoaded, timeoutMinutes, warningSeconds, resetTimer, handleActivity]);

  return { showWarning, handleStayLoggedIn, handleLogout };
};
